import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../theme";
import TimerIcon from "@mui/icons-material/Timer";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

export default function RollbackOtpModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  error = "",
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(180); // 3 dakika = 180 saniye
  const [timerActive, setTimerActive] = useState(false);

  // Timer başlat
  useEffect(() => {
    if (open && !timerActive) {
      setTimeLeft(180);
      setTimerActive(true);
      setOtp("");
    }
  }, [open, timerActive]);

  // Timer countdown
  useEffect(() => {
    if (!timerActive || timeLeft <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, timerActive]);

  // Modal kapatıldığında state'i temizle
  useEffect(() => {
    if (!open) {
      setTimerActive(false);
      setOtp("");
      setTimeLeft(180);
    }
  }, [open]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (otp.length === 6 && timeLeft > 0 && !loading) {
      onConfirm(otp);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[500],
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${colors.primary[200]}30`,
          boxShadow: `0 18px 40px ${colors.primary[900]}60`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 90%)`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: `${colors.primary[500]}aa`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 10px 22px ${colors.primary[900]}45`,
          }}
        >
          <VpnKeyIcon
            sx={{ fontSize: 28, color: colors.blueAccent[200] }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{ color: colors.grey[100], fontWeight: 700, letterSpacing: 0.4 }}
        >
          Confirm Rollback Operation
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3.5,
          py: 3,
          backgroundColor: colors.primary[500],
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: colors.grey[200],
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Enter the 6-digit confirmation code sent to the tenant for this operation
          </Typography>

          {/* Timer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mb: 3,
              p: 2,
              borderRadius: "12px",
              background:
                timeLeft <= 30
                  ? `linear-gradient(135deg, ${colors.redAccent[600]}, ${colors.redAccent[500]})`
                  : `linear-gradient(135deg, ${colors.blueAccent[600]}, ${colors.greenAccent[600]})`,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            }}
          >
            <TimerIcon sx={{ color: "white", fontSize: 28 }} />
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 700,
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>

          {/* OTP Input */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: colors.grey[300],
                textAlign: "center",
                fontWeight: 600,
              }}
            >
              Confirmation Code (6 Digits)
            </Typography>
            <TextField
              fullWidth
              required
              value={otp}
              placeholder="000000"
              onChange={handleOtpChange}
              onKeyPress={handleKeyPress}
              disabled={timeLeft === 0 || loading}
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: "center",
                  fontSize: "2rem",
                  letterSpacing: "0.5rem",
                  fontWeight: 600,
                  color: colors.grey[100],
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.primary[600],
                  "& fieldset": {
                    borderColor: colors.grey[700],
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.blueAccent[400],
                  },
                },
              }}
              autoFocus
            />
          </Box>

          {/* Info Message */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: colors.grey[400],
              mb: 2,
            }}
          >
            OTP code has been sent to the tenant's email address. The code is valid for 3 minutes.
          </Typography>

          {/* Error Message */}
          {error && (
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: "8px",
                backgroundColor: `${colors.redAccent[900]}40`,
                border: `1px solid ${colors.redAccent[700]}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.redAccent[300],
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
            </Box>
          )}

          {/* Time Expired Warning */}
          {timeLeft === 0 && (
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: "8px",
                backgroundColor: `${colors.redAccent[900]}40`,
                border: `1px solid ${colors.redAccent[700]}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.redAccent[300],
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                OTP has expired. Please request a new code.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: `${colors.primary[600]}55`,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            flex: 1,
            borderRadius: 3,
            borderColor: colors.blueAccent[400],
            color: colors.blueAccent[200],
            fontWeight: 700,
            textTransform: "none",
            letterSpacing: 0.3,
            "&:hover": {
              borderColor: colors.blueAccent[300],
              backgroundColor: `${colors.blueAccent[700]}35`,
            },
            "&:disabled": {
              borderColor: colors.grey[700],
              color: colors.grey[500],
            },
          }}
          >
            Cancel
          </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={otp.length !== 6 || timeLeft === 0 || loading}
          sx={{
            flex: 1,
            borderRadius: 3,
            textTransform: "none",
            letterSpacing: 0.3,
            fontWeight: 700,
            color: colors.grey[100],
            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`,
            boxShadow: `0 12px 28px ${colors.greenAccent[900]}45`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 16px 32px ${colors.greenAccent[900]}60`,
            },
            "&:disabled": {
              background: colors.grey[700],
              color: colors.grey[500],
              transform: "none",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: colors.grey[100] }} />
              <span>Processing...</span>
            </Box>
          ) : (
            "Confirm"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

